import { reactive } from 'vue';

/**
 * Store reattivo condiviso per gestire gli aggiornamenti di token in tempo reale
 * Permette a componenti diversi (es. WidgetPreview, TokenBadge) di sincronizzarsi
 * senza bisogno di una architettura più complessa come Pinia
 */

const TOKENS_PER_EURO = Number(import.meta.env.VITE_TOKENS_PER_EURO) || 5_000_000

export const toEuro = (tokens) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format((tokens || 0) / TOKENS_PER_EURO)

let tokenStore = null;

function initializeTokenStore() {
  if (!tokenStore) {
    tokenStore = reactive({
      tokenUsed: 0,
      tokenAllowance: 0,
      tokenRemaining: 0,
      isUnlimited: false,
      isNearLimit: false,
      hasExceeded: false,
      managed: false,
      consumptionPercentage: null,
      lastUpdatedAt: null,
      euroUsato: '€0,00',
      euroTotale: '€0,00',
      euroResiduo: '€0,00',
    });
  }
  return tokenStore;
}

export function useTokenStore() {
  const store = initializeTokenStore();

  /**
   * Aggiorna lo store dei token con nuovi dati
   * Tipicamente chiamato da WidgetPreview quando riceve aggiornamenti dai messaggi
   */
  const updateTokens = (data) => {
    if (!data) return;

    // Aggiorna i campi
    if (data.tokenUsed !== undefined) store.tokenUsed = data.tokenUsed;
    if (data.tokenAllowance !== undefined) store.tokenAllowance = data.tokenAllowance;
    if (data.tokenRemaining !== undefined) store.tokenRemaining = data.tokenRemaining;
    if (data.isUnlimited !== undefined) store.isUnlimited = data.isUnlimited;
    if (data.managed !== undefined) store.managed = data.managed;

    // Ricalcola gli stati se non forniti
    if (data.isUnlimited === undefined) {
      store.isUnlimited = store.tokenAllowance === -1;
    }

    if (data.hasExceeded === undefined) {
      store.hasExceeded = !store.isUnlimited && store.tokenRemaining <= 0;
    } else {
      store.hasExceeded = data.hasExceeded;
    }

    if (data.isNearLimit === undefined) {
      store.isNearLimit = !store.isUnlimited &&
                          !store.hasExceeded &&
                          (store.tokenUsed / store.tokenAllowance) >= 0.8;
    } else {
      store.isNearLimit = data.isNearLimit;
    }

    // consumptionPercentage: usa il valore del server se presente, altrimenti
    // derivalo dai token correnti così resta aggiornato anche sugli update WebSocket
    if (data.consumptionPercentage !== undefined) {
      store.consumptionPercentage = data.consumptionPercentage;
    } else {
      store.consumptionPercentage = (!store.isUnlimited && store.tokenAllowance > 0)
        ? Math.min(100, Math.round((store.tokenUsed / store.tokenAllowance) * 100))
        : null;
    }

    store.lastUpdatedAt = new Date().toISOString();
    store.euroUsato = toEuro(store.tokenUsed);
    store.euroTotale = store.isUnlimited ? '∞' : toEuro(store.tokenAllowance);
    store.euroResiduo = store.isUnlimited ? '∞' : toEuro(Math.max(0, store.tokenRemaining));
  };

  /**
   * Incrementa i token usati (per aggiornamenti incrementali)
   */
  const addTokenUsage = (amount) => {
    store.tokenUsed += amount;
    if (!store.isUnlimited) {
      store.tokenRemaining = Math.max(0, store.tokenRemaining - amount);
      store.hasExceeded = store.tokenRemaining <= 0;
      store.isNearLimit = !store.hasExceeded && (store.tokenUsed / store.tokenAllowance) >= 0.8;
    }
    store.consumptionPercentage = (!store.isUnlimited && store.tokenAllowance > 0)
      ? Math.min(100, Math.round((store.tokenUsed / store.tokenAllowance) * 100))
      : null;
    store.euroUsato = toEuro(store.tokenUsed);
    store.euroResiduo = store.isUnlimited ? '∞' : toEuro(Math.max(0, store.tokenRemaining));
  };

  /**
   * Reset dello store (útile per testing o logout)
   */
  const reset = () => {
    store.tokenUsed = 0;
    store.tokenAllowance = 0;
    store.tokenRemaining = 0;
    store.isUnlimited = false;
    store.isNearLimit = false;
    store.hasExceeded = false;
    store.managed = false;
    store.consumptionPercentage = null;
    store.lastUpdatedAt = null;
  };

  return {
    store,
    updateTokens,
    addTokenUsage,
    reset
  };
}
